import React, { useState } from "react";
import {
  Button,
  Text,
  Group,
  Container,
  useComputedColorScheme,
  useMantineTheme,
  RingProgress,
  Pagination,
  Stack,
  Title,
  Tooltip,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  HiChevronLeft,
  HiChevronRight,
  HiHome,
  HiCollection,
  HiRefresh,
  HiCheck,
} from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import TravelAnimationSection from "../sections/TravelAnimationSection";
import GallerySection, {
  GallerySectionProps,
} from "../sections/GallerySection";
import SplitSection, { SplitSectionProps } from "../sections/SplitSection";
import TextSection, { TextSectionProps } from "../sections/TextSection";
import { FullScreen, useFullScreenHandle } from "react-full-screen";

// Define the types for section configuration
export interface SectionConfig {
  id: string;
  type: "travel" | "gallery" | "split" | "text";
  title?: string;
  data: any; // This will contain section-specific data
}

// Define specific data types for each section
export interface TravelSectionData {
  origin: number[];
  destination: number[];
  autoPlay?: boolean;
  autoPlayDelayMs?: number;
  interactive?: boolean;
  showRouteLine?: boolean;
  projection?: "globe" | "mercator" | undefined;
  zoom?: number;
}

export interface GallerySectionData {
  folderId: string;
  pageSize?: number;
  enableInfiniteLoading?: boolean;
  isSelectionEnabled?: boolean;
  hiddenOptions?: boolean;
  isMiniGallery?: boolean;
  hardcodeSettings?: any;
}

export interface SplitSectionData {
  leftSection: SectionConfig;
  rightSection: SectionConfig;
  leftWidth?: number; // Percentage (0-100)
  rightWidth?: number; // Percentage (0-100)
  gap?: number; // Gap between sections in pixels
}

export interface TextSectionData {
  content?: string;
  isEditable?: boolean;
  placeholder?: string;
  showToolbar?: boolean;
  variant?: "default" | "subtle";
  onContentChange?: (content: string) => void;
}

export interface AlbumSlideshowProps {
  title: string;
  sections: SectionConfig[];
  autoPlay?: boolean;
  autoPlayDelayMs?: number;
}

export default function AlbumSlideshow({
  title,
  sections,
  autoPlay = false,
  autoPlayDelayMs = 3000,
}: AlbumSlideshowProps) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const colorScheme = useComputedColorScheme();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const fullScreenHandle = useFullScreenHandle();

  const goToNext = () => {
    // Check if we're at the last section
    if (currentSlide >= sections.length) {
      return; // Already at the end
    }

    if (currentSlide === sections.length - 1) {
      // Move to the end of album section
      setCurrentSlide(sections.length);
    } else {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const goToPrevious = () => {
    if (currentSlide <= 0) {
      return; // Already at the beginning
    }

    setCurrentSlide((prev) => prev - 1);
  };

  const renderSection = (section: SectionConfig) => {
    switch (section.type) {
      case "travel":
        return (
          <TravelAnimationSection
            uniqueMapId={section.id}
            origin={section.data.origin}
            destination={section.data.destination}
            autoPlay={section.data.autoPlay || autoPlay}
            autoPlayDelayMs={section.data.autoPlayDelayMs || autoPlayDelayMs}
            interactive={section.data.interactive || false}
            showRouteLine={section.data.showRouteLine || true}
            projection={section.data.projection || "mercator"}
            zoom={section.data.zoom || 3}
          />
        );
      case "gallery":
        return (
          <GallerySection
            folderId={section.data.folderId}
            title={section.title}
            pageSize={section.data.pageSize}
            enableInfiniteLoading={section.data.enableInfiniteLoading}
            isSelectionEnabled={section.data.isSelectionEnabled}
            hiddenOptions={section.data.hiddenOptions}
            isMiniGallery={section.data.isMiniGallery}
            hardcodeSettings={section.data.hardcodeSettings}
          />
        );
      case "split":
        return (
          <SplitSection
            title={section.title}
            leftSection={section.data.leftSection}
            rightSection={section.data.rightSection}
            leftWidth={section.data.leftWidth}
            rightWidth={section.data.rightWidth}
            gap={section.data.gap}
            autoPlay={autoPlay}
            autoPlayDelayMs={autoPlayDelayMs}
          />
        );
      case "text":
        return (
          <TextSection
            title={section.title}
            content={section.data.content}
            isEditable={section.data.isEditable}
            placeholder={section.data.placeholder}
            showToolbar={section.data.showToolbar}
            variant={section.data.variant}
            onContentChange={section.data.onContentChange}
          />
        );
      default:
        return (
          <div className="flex flex-col w-full h-full gap-4">
            <Text fz="md" c="dimmed">
              Unknown Section Type
            </Text>
            <div className="w-full h-full rounded-lg border border-grey-800 overflow-hidden flex items-center justify-center">
              <Text c="dimmed">Unknown section type: {section.type}</Text>
            </div>
          </div>
        );
    }
  };

  const renderAlbumProgressRing = () => {
    // Calculate progress including the end section
    const totalSlides = sections.length + 1; // +1 for end section
    const value = (currentSlide / totalSlides) * 100;
    return (
      <RingProgress
        size={30}
        thickness={4}
        sections={[
          {
            value: value,
            color: value === 100 ? "green" : "blue",
          },
        ]}
      ></RingProgress>
    );
  };

  const renderPagination = () => {
    if (isMobile) {
      const isLastSection = currentSlide === sections.length - 1;
      return (
        <div className="border-t border-grey-200 dark:border-grey-700 p-2 flex justify-center max-h-min gap-2">
          <Button
            onClick={goToPrevious}
            variant="light"
            fullWidth
            className="flex-1"
            disabled={currentSlide <= 0}
          >
            <HiChevronLeft size={20} />
          </Button>

          <Button
            onClick={goToNext}
            color={isLastSection ? "green" : "blue"}
            variant="light"
            fullWidth
            className="flex-1"
          >
            {isLastSection ? (
              <HiCheck size={20} />
            ) : (
              <HiChevronRight size={20} />
            )}
          </Button>
        </div>
      );
    }

    return (
      <div className="border-t border-grey-200 dark:border-grey-700 p-4 flex justify-center max-h-min">
        <Pagination.Root
          value={currentSlide + 1}
          onChange={(value) => {
            setCurrentSlide(value - 1);
          }}
          total={sections.length}
          size={isMobile ? "lg" : "md"}
          boundaries={2}
        >
          <Group gap={5} justify="center">
            {/* <Pagination.First /> */}
            <Pagination.Previous />
            <Pagination.Items />
            <Pagination.Next disabled={isEndOfAlbum} onClick={goToNext} />
            {/* <Pagination.Last /> */}
          </Group>
        </Pagination.Root>
      </div>
    );
  };

  if (!sections || sections.length === 0) {
    return (
      <div className="flex flex-col w-full h-full gap-4">
        <Text fz="md" c="dimmed">
          No sections available
        </Text>
        <div className="w-full h-full rounded-lg border border-grey-800 overflow-hidden flex items-center justify-center">
          <Text c="dimmed">No sections to display</Text>
        </div>
      </div>
    );
  }

  // Check if we're at the end section
  const isEndOfAlbum = currentSlide >= sections.length;

  // If at the end, render the end section inline
  if (isEndOfAlbum) {
    return (
      <div className="flex flex-col w-full h-full items-center justify-center p-8">
        <Container size="sm">
          <Stack align="center" gap="xl">
            {/* Icon or visual element */}
            <div className="text-6xl">
              <HiCollection className="mx-auto" />
            </div>

            {/* Title */}
            <Title order={2} ta="center" size={isMobile ? "h3" : "h3"}>
              {"End of Album"}
            </Title>

            {/* Message */}
            <Text
              c="dimmed"
              size={isMobile ? "md" : "md"}
              ta="center"
              style={{ maxWidth: "500px" }}
            >
              {"You've reached the end of this album. Thank you for viewing!"}
            </Text>

            {/* Action buttons */}
            <Stack gap="sm" mt="md">
              <Button
                size={isMobile ? "sm" : "sm"}
                leftSection={<HiCollection size={20} />}
                onClick={() => navigate("/albums")}
                variant="filled"
                fullWidth={isMobile}
              >
                {"Return to Albums"}
              </Button>

              <Button
                size={isMobile ? "sm" : "sm"}
                leftSection={<HiRefresh size={18} />}
                onClick={() => setCurrentSlide(0)}
                variant="subtle"
                fullWidth={isMobile}
              >
                View again
              </Button>
            </Stack>
          </Stack>
        </Container>
      </div>
    );
  }

  const currentSection = sections[currentSlide];

  return (
    <div className="flex flex-col w-full h-full">
      {/* Header with current slide info */}
      <div className="flex justify-between items-center max-h-fit p-4 border-b border-grey-200 dark:border-grey-700">
        <div className="flex flex-col w-full">
          <Text fz="xs" c="dimmed">
            {title || `Slide ${currentSlide + 1}`}
          </Text>
          <div className="flex flex-row justify-between items-center">
            <Text fz="lg" fw={500}>
              {currentSection.title || `Slide ${currentSlide + 1}`}
            </Text>
            {/* <Text fz="sm" c="dimmed">
              {currentSlide + 1} of {sections.length}
            </Text> */}
          </div>
        </div>
        {renderAlbumProgressRing()}
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-auto">
        {renderSection(currentSection)}
      </div>

      {/* Bottom navigation bar */}
      {renderPagination()}

      {/* <FullScreen handle={fullScreenHandle}>
      </FullScreen> */}
    </div>
  );
}
